import { axiosInstance } from "./AxiosDefaults";
import { settingsRoute } from "../constants/ApiRoutes";
import { ParamSettings } from "../models/ParamSettings";

export type ApiResponse<T> = {
  status?: "SUCCESS" | "ERROR";
  message?: string;
  result?: T;
};

const toNum = (v: any): number => {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toOptNum = (v: any): number | undefined => {
  if (v === null || v === undefined || v === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const toBool = (v: any): boolean | undefined => {
  if (v === null || v === undefined) return undefined;
  if (typeof v === "boolean") return v;
  if (v === "true" || v === "1" || v === 1) return true;
  if (v === "false" || v === "0" || v === 0) return false;
  return Boolean(v);
};

export const normalizeParamSettings = (s: any): ParamSettings => {
  return {
    ...s,
    moisture: toNum(s?.moisture),
    moistureMax: toNum(s?.moistureMax),
    moistureMin: toNum(s?.moistureMin),
    impurity: toNum(s?.impurity),
    impurityMax: toNum(s?.impurityMax),
    impurityMin: toNum(s?.impurityMin),
    breakage: toOptNum(s?.breakage),
    breakageMax: toOptNum(s?.breakageMax),
    breakageMin: toOptNum(s?.breakageMin),
    defect: toOptNum(s?.defect),
    defectMax: toOptNum(s?.defectMax),
    defectMin: toOptNum(s?.defectMin),
    hectoliter: toOptNum(s?.hectoliter),
    hectoliterMax: toOptNum(s?.hectoliterMax),
    hectoliterMin: toOptNum(s?.hectoliterMin),
    calculateCalo: toBool(s?.calculateCalo),
  } as ParamSettings;
};

export const getAllSettings = async () => {
  const resp = await axiosInstance.get<ApiResponse<any[]>>(settingsRoute, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });

  if (resp?.data?.result && Array.isArray(resp.data.result)) {
    resp.data.result = resp.data.result.map(normalizeParamSettings);
  }

  return resp as unknown as { data: ApiResponse<ParamSettings[]> };
};

export const getOneSettings = async (id: string) => {
  const resp = await axiosInstance.get<ApiResponse<any>>(
    `${settingsRoute}/${id}`,
    {
      withCredentials: false,
      headers: { "Content-Type": "application/json" },
    }
  );

  if (resp?.data?.result) {
    resp.data.result = normalizeParamSettings(resp.data.result);
  }

  return resp as unknown as { data: ApiResponse<ParamSettings> };
};

export const createSettings = async (payload: Partial<ParamSettings>) => {
  const resp = await axiosInstance.post<ApiResponse<any>>(
    settingsRoute,
    payload,
    {
      withCredentials: false,
      headers: { "Content-Type": "application/json" },
    }
  );

  if (resp?.data?.result) {
    resp.data.result = normalizeParamSettings(resp.data.result);
  }

  return resp as unknown as { data: ApiResponse<ParamSettings> };
};

export const updateOneSettings = async (
  id: string,
  payload: Partial<ParamSettings>
) => {
  const resp = await axiosInstance.put<ApiResponse<any>>(
    `${settingsRoute}/${id}`,
    payload,
    {
      withCredentials: false,
      headers: { "Content-Type": "application/json" },
    }
  );

  if (resp?.data?.result) {
    resp.data.result = normalizeParamSettings(resp.data.result);
  }

  return resp as unknown as { data: ApiResponse<ParamSettings> };
};
